// eTAMS — Receipt centre (PDF page 7)
import React, { useEffect, useMemo, useState } from 'react';
import { etamsAPI, expenseAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Badge, Field, Input, Select, Note, Empty, Loading, Spin, FileDrop, Kebab, Tabs,
  useAsync, useAction, confirmAction, uploadFiles, money, isoDate, sizeLabel,
} from '../../components/ui';
import { notify } from '../../utils/notify';
import { DOCUMENT_TYPES, DOC_TO_CATEGORY, idOf } from '../../utils/etams';

const guessType = (name = '') => {
  const n = name.toLowerCase();
  if (/hotel|inn|stay|lodg/.test(n)) return 'Hotel invoice';
  if (/train|rail|irctc|pnr|ticket/.test(n)) return 'Rail ticket';
  if (/air|flight|indigo|vistara/.test(n)) return 'Air ticket';
  if (/cab|uber|ola|taxi/.test(n)) return 'Cab receipt';
  if (/food|meal|restaurant/.test(n)) return 'Food bill';
  return 'Other';
};
const CATEGORY_TONE = { Hotel: 'teal', 'Rail Fare': 'green', 'Air Ticket': 'blue', 'Local Conveyance': 'amber', 'Food + OPE': 'purple' };

// Signed-URL thumbnail cache for image receipts
const thumbCache = {};
function Thumb({ file }) {
  const [url, setUrl] = useState(thumbCache[file?.s3Key] || null);
  const isImage = /\.(png|jpe?g|webp|gif)$/i.test(file?.fileName || '') || (file?.fileType || '').startsWith('image/');
  useEffect(() => {
    if (!isImage || !file?.s3Key || url) return;
    expenseAPI.getAttachmentUrl(file.s3Key).then((d) => { thumbCache[file.s3Key] = d.url; setUrl(d.url); }).catch(() => {});
  }, [file?.s3Key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ height: 190, border: '1px solid #e1e9f1', borderRadius: 6, background: '#f7f9fb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {isImage && url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <div style={{ textAlign: 'center', color: '#64768a' }}><i className={`bi ${/\.pdf$/i.test(file?.fileName || '') ? 'bi-file-earmark-pdf' : 'bi-file-earmark-image'}`} style={{ fontSize: '2.34rem', color: /\.pdf$/i.test(file?.fileName || '') ? '#d63a2f' : '#1479bd' }} /><div className="tse-small">{sizeLabel(file?.size)}</div></div>}
    </div>
  );
}

export default function ReceiptCentre() {
  const { user } = useAuth();
  const list = useAsync(() => etamsAPI.listReceipts(), []);
  const claims = useAsync(() => expenseAPI.listClaims({ limit: 200 }).then((d) => (d.claims || []).filter((c) => idOf(c.requestedBy) === String(user?.id) && !['SETTLED', 'REJECTED'].includes(c.status))), [], { silent: true });
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('all');
  const [busy, run] = useAction();
  const receipts = list.data?.receipts || [];

  useEffect(() => {
    const r = receipts.find((x) => x._id === selected);
    if (r) setForm({ documentType: r.documentType, expenseDate: isoDate(r.expenseDate), billNumber: r.billNumber || '', vendorName: r.vendorName || '', vendorGstin: r.vendorGstin || '', amount: r.amount || '', gst: r.gst || '', linkedClaimId: r.linkedClaimId || '' });
  }, [selected, list.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFiles = async (files) => {
    setUploading(true);
    try {
      const atts = await uploadFiles(files, 'etams');
      let last;
      for (const a of atts) {
        const docType = guessType(a.fileName);
        // eslint-disable-next-line no-await-in-loop
        const r = await etamsAPI.createReceipt({ file: a, documentType: docType, category: DOC_TO_CATEGORY[docType] });
        last = r.receipt;
      }
      notify.success(`${atts.length} file${atts.length > 1 ? 's' : ''} uploaded — add details on the right`);
      await list.reload();
      if (last) setSelected(last._id);
    } catch (e) {
      notify.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveDetails = async () => {
    const r = await run('save', () => etamsAPI.updateReceipt(selected, {
      ...form, category: DOC_TO_CATEGORY[form.documentType], amount: Number(form.amount) || 0, gst: Number(form.gst) || 0,
      linkedClaimId: form.linkedClaimId || null,
    }), 'Details saved');
    if (r) list.reload();
  };
  const assign = async (rc, claimId) => {
    const r = await run(rc._id, () => etamsAPI.assignReceipt(rc._id, claimId));
    if (r) list.reload();
  };
  const remove = async (rc) => {
    if (!(await confirmAction('Delete receipt?', rc.file?.fileName, 'Delete', '#c43c31'))) return;
    const r = await run(rc._id, () => etamsAPI.deleteReceipt(rc._id), 'Receipt deleted');
    if (r) { if (selected === rc._id) setSelected(null); list.reload(); }
  };
  const preview = (rc) => expenseAPI.getAttachmentUrl(rc.file.s3Key).then((d) => window.open(d.url, '_blank')).catch(() => notify.error('Could not open file'));

  const shown = useMemo(() => receipts.filter((r) => tab === 'all' || (tab === 'unassigned' ? !r.linkedClaimId : !!r.linkedClaimId)), [receipts, tab]);
  const claimOptions = (claims.data || []).map((c) => ({ value: c._id, label: `${c.claimId} · ${c.reportTitle || c.purposeOfTravel}` }));
  const sel = receipts.find((x) => x._id === selected);

  return (
    <PageShell section="Travel & Expenses">
      <div className="tse-split wide-side">
        <div>
          <PageHead title="Receipt centre" subtitle="Attach bills, travel tickets and supporting documents." />
          <FileDrop onFiles={onFiles} accept=".pdf,.jpg,.jpeg,.png" title="Add receipts or travel tickets" sub="PDF, JPG or PNG · Up to 10 MB" button={uploading ? 'Uploading…' : 'Select files'} disabled={uploading} />
          <div style={{ margin: '13px 0 10px' }}>
            <Tabs value={tab} onChange={setTab} tabs={[
              { key: 'all', label: 'All receipts', count: receipts.length },
              { key: 'unassigned', label: 'Unassigned', count: receipts.filter((r) => !r.linkedClaimId).length },
              { key: 'assigned', label: 'Assigned to claims', count: receipts.filter((r) => r.linkedClaimId).length },
            ]} />
          </div>
          {list.loading ? <Loading /> : !shown.length ? <Card><Empty icon="bi-receipt" title="No receipts yet" sub="Upload hotel invoices, tickets and cab receipts, then assign them to a claim." /></Card> : (
            <div className="tse-grid g-3">
              {shown.map((rc) => (
                <div key={rc._id} className="tse-card" style={{ outline: selected === rc._id ? '2px solid #1479bd' : 'none', cursor: 'pointer' }} onClick={() => setSelected(rc._id)}>
                  <div className="tse-card-body tight">
                    <Thumb file={rc.file} />
                    <div className="tse-row" style={{ justifyContent: 'space-between', marginTop: 8, flexWrap: 'nowrap' }}>
                      <div className="tse-strong tse-ellipsis" title={rc.file?.fileName}>{rc.file?.fileName}</div>
                      <Kebab items={[{ label: 'Delete', icon: 'bi-trash', danger: true, onClick: () => remove(rc) }, { label: 'Unassign from claim', icon: 'bi-x-circle', hidden: !rc.linkedClaimId, onClick: () => assign(rc, null) }]} />
                    </div>
                    <div style={{ fontSize: '1.248rem', fontWeight: 700, color: '#1479bd' }}>{money(rc.amount)}</div>
                    <div className="tse-row" style={{ gap: 8, margin: '3px 0 4px' }}>
                      <Badge tone={CATEGORY_TONE[rc.category] || 'gray'} pill>{rc.category || rc.documentType}</Badge>
                    </div>
                    <div className="tse-muted tse-small" style={{ minHeight: 18 }}>{rc.linkedClaimNo || 'Not assigned'}</div>
                    <div className="tse-row" style={{ gap: 8, marginTop: 10, flexWrap: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="tse-btn sm outline" style={{ flex: 1 }} onClick={() => preview(rc)}><i className="bi bi-eye" />Preview</button>
                      <button type="button" className="tse-btn sm" style={{ flex: 1.3 }} onClick={() => setSelected(rc._id)}>{rc.linkedClaimId ? 'Change claim' : 'Assign to claim'}</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Card navy title="Attachment details" style={{ position: 'sticky', top: 84 }}>
          {!sel ? (
            <Empty icon="bi-hand-index" title="Select a receipt" sub="Choose a receipt card to add its details and link it to an expense claim." />
          ) : (
            <div className="tse-stack" style={{ gap: 8 }}>
              <Field label="Document type"><Select value={form.documentType || ''} onChange={(e) => setForm((p) => ({ ...p, documentType: e.target.value }))} placeholder="Select document type" options={DOCUMENT_TYPES} /></Field>
              <Field label="Expense date"><Input type="date" value={form.expenseDate || ''} onChange={(e) => setForm((p) => ({ ...p, expenseDate: e.target.value }))} /></Field>
              <Field label="Bill number"><Input value={form.billNumber || ''} placeholder="Enter bill number" onChange={(e) => setForm((p) => ({ ...p, billNumber: e.target.value }))} /></Field>
              <div className="tse-form-grid c2">
                <Field label="Amount (₹)"><Input type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} /></Field>
                <Field label="of which GST (₹)"><Input type="number" value={form.gst} onChange={(e) => setForm((p) => ({ ...p, gst: e.target.value }))} /></Field>
              </div>
              <Field label="Vendor GSTIN" hint="15-character GSTIN, optional"><Input value={form.vendorGstin || ''} placeholder="Enter vendor GSTIN (optional)" onChange={(e) => setForm((p) => ({ ...p, vendorGstin: e.target.value.toUpperCase() }))} maxLength={15} /></Field>
              <Field label="Linked claim"><Select value={form.linkedClaimId || ''} onChange={(e) => setForm((p) => ({ ...p, linkedClaimId: e.target.value }))} placeholder="Select expense claim" options={claimOptions} /></Field>
              <button type="button" className="tse-btn lg block" onClick={saveDetails} disabled={busy === 'save'}>{busy === 'save' ? <Spin /> : null}Save details</button>
              <hr className="tse-divider" style={{ margin: '3px 0' }} />
              <Note tone="plain">Receipts linked to a claim are added to its supporting documents for the approver and Accounts.</Note>
            </div>
          )}
        </Card>
      </div>
    </PageShell>
  );
}
